import { Button, ListGroup, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { faEye, faUserCircle } from '@fortawesome/free-regular-svg-icons';
import {
  faAlignLeft,
  faChevronRight,
  faGear,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import GradingReceivedNumberBadge from 'src/components/GradingReceivedNumberBadge';

import { I18NextNS } from 'src/models/Language.model';
import { Product } from 'src/models/Product.model';

import useCheckNavigation from 'src/hooks/useCheckNavigation';
import { useCheckNavItemVisibility } from 'src/hooks/useCheckNavItemVisibility';
import useGetGradingReceivedCount from 'src/hooks/useGetGradingReceivedCount';

import { getSessionPage } from 'src/utils/navigatePages';

const Sidebar = () => {
  const { hasExpandedSidebar, setHasExpandedSidebar } = useLocalStore();

  const { isShowNewExamPage, isShowResultsPage, isShowUsersPage } =
    useCheckNavItemVisibility();

  const { checkNavigation } = useCheckNavigation();

  const { t } = useTranslation(I18NextNS.COMMON, { keyPrefix: 'tabs' });

  const {
    practiceSettings: { product },
  } = useRemoteBoundStore();

  useGetGradingReceivedCount();

  return (
    <ListGroup
      className={classNames(
        'h-100 d-flex flex-column border-end border-primary rounded-5 position-relative border-top border-bottom',
      )}
      style={{
        width: hasExpandedSidebar ? '150px' : '50px',
        transition: 'width 0.3s ease',
      }}
    >
      <div className="overflow-hidden rounded-5 h-100 d-flex flex-column">
        {isShowNewExamPage && (
          <ListGroup.Item
            as={NavLink}
            to="/session-form"
            exact={true}
            className="flex-grow-1 d-flex align-items-center justify-content-center text-primary border-primary text-center border-bottom border-0"
            activeClassName="active bg-primary text-white"
            onClick={checkNavigation}
            action
            isActive={getSessionPage}
          >
            {hasExpandedSidebar ? (
              <Stack
                className="align-items-center justify-content-center"
                gap={2}
              >
                <FontAwesomeIcon icon={faEye} />
                <div>{t('examOrders')}</div>
              </Stack>
            ) : (
              <FontAwesomeIcon icon={faEye} />
            )}
          </ListGroup.Item>
        )}

        {isShowResultsPage && (
          <ListGroup.Item
            as={NavLink}
            to="/results"
            exact={true}
            className="flex-grow-1 d-flex align-items-center justify-content-center text-primary border-primary  text-center border-bottom border-0"
            activeClassName="active bg-primary text-white"
            onClick={checkNavigation}
            action
          >
            {hasExpandedSidebar ? (
              <Stack
                className="align-items-center justify-content-center"
                gap={2}
              >
                <FontAwesomeIcon icon={faAlignLeft} />
                <div className="position-relative">
                  {t('results')}
                  {product === Product.TELEOPHTH_STANDALONE && (
                    <GradingReceivedNumberBadge className="position-absolute bottom-100 start-100" />
                  )}
                </div>
              </Stack>
            ) : (
              <div>
                {product === Product.TELEOPHTH_STANDALONE && (
                  <GradingReceivedNumberBadge />
                )}
                <FontAwesomeIcon icon={faAlignLeft} />
              </div>
            )}
          </ListGroup.Item>
        )}

        <ListGroup.Item
          as={NavLink}
          to="/settings"
          exact={true}
          className={classNames(
            'flex-grow-1 d-flex align-items-center justify-content-center text-primary text-center border-primary border-0',
            {
              'border-bottom': isShowUsersPage,
            },
          )}
          activeClassName="active bg-primary text-white"
          onClick={checkNavigation}
          action
          data-test="settings-sidebar-nav"
        >
          {hasExpandedSidebar ? (
            <Stack
              className="align-items-center justify-content-center"
              gap={2}
            >
              <FontAwesomeIcon icon={faGear} />
              <div>{t('settings')}</div>
            </Stack>
          ) : (
            <FontAwesomeIcon icon={faGear} />
          )}
        </ListGroup.Item>

        {isShowUsersPage && (
          <ListGroup.Item
            as={NavLink}
            to="/subuser"
            exact={true}
            className="flex-grow-1 d-flex align-items-center justify-content-center text-primary text-center"
            activeClassName="active bg-primary text-white"
            onClick={checkNavigation}
            action
          >
            {hasExpandedSidebar ? (
              <Stack
                className="align-items-center justify-content-center"
                gap={2}
              >
                <FontAwesomeIcon icon={faUserCircle} />
                <div>{t('users')}</div>
              </Stack>
            ) : (
              <FontAwesomeIcon icon={faUserCircle} />
            )}
          </ListGroup.Item>
        )}
      </div>

      <div
        className="position-absolute z-3"
        style={{ top: '36px', right: '-16px', cursor: 'pointer' }}
      >
        <Button
          className="py-1 bg-white border rounded-2 border-light-subtle p-3"
          onClick={() => setHasExpandedSidebar(!hasExpandedSidebar)}
        >
          <FontAwesomeIcon
            icon={faChevronRight}
            className="text-black-50"
            size="xs"
            style={{
              transform: hasExpandedSidebar ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform .3s ease',
            }}
          />
        </Button>
      </div>
    </ListGroup>
  );
};

export default Sidebar;
